import type { BoardColumn, BoardPlacement, SupervisorReminder, Task, Uuid } from './db';

/**
 * data-model.md 3.2、5.2、6.1、6.2 的不變式。
 *
 * 這裡只做**純資料**檢查，不碰資料庫。需要跨表查詢的條件
 * （T7 linkedTaskId 指向存在的任務、T8 number 唯一）由 repository 層負責，
 * 因為它們需要交易內的一致性視角。
 */

export interface InvariantViolation {
  code: string;
  message: string;
}

export class InvariantError extends Error {
  readonly code: string;
  readonly violations: InvariantViolation[];

  constructor(violations: InvariantViolation[]) {
    const first = violations[0];
    super(first ? `${first.code}：${first.message}` : '不變式檢查失敗');
    this.name = 'InvariantError';
    this.code = first?.code ?? 'UNKNOWN';
    this.violations = violations;
  }
}

const COMPLETED_STATUSES = new Set(['resolved', 'closed']);

/** 便條不得使用的任務專屬欄位（T5）。 */
const NOTE_FORBIDDEN_FIELDS = [
  'taskTypeId',
  'sourceTypeId',
  'progress',
  'estimatedMinutes',
  'actualMinutes',
  'recurrenceRuleId',
] as const;

export function checkTask(task: Task): InvariantViolation[] {
  const violations: InvariantViolation[] = [];
  const hasCompleted = task.completedAt !== null;
  const hasCancelled = task.cancelledAt !== null;

  if (hasCompleted && hasCancelled) {
    violations.push({
      code: 'T1',
      message: 'completedAt 與 cancelledAt 不可同時有值：做完了與不做了是兩件事',
    });
  }

  if (COMPLETED_STATUSES.has(task.status) !== hasCompleted) {
    violations.push({
      code: 'T2',
      message: 'status 為 resolved 或 closed 時必須有 completedAt，反之亦然',
    });
  }

  if ((task.status === 'cancelled') !== hasCancelled) {
    violations.push({
      code: 'T3',
      message: 'status 為 cancelled 時必須有 cancelledAt，反之亦然',
    });
  }

  if ((task.status === 'waiting') !== (task.waitingSince !== null)) {
    violations.push({
      code: 'T4',
      message: 'status 為 waiting 時必須有 waitingSince，離開時必須清空',
    });
  }

  if (task.kind === 'note') {
    const used = NOTE_FORBIDDEN_FIELDS.filter((field) => task[field] !== null);
    if (used.length > 0) {
      violations.push({ code: 'T5', message: `便條不得使用 ${used.join('、')}` });
    }
  }

  if (task.kind === 'task' && task.linkedTaskId !== null) {
    violations.push({ code: 'T6', message: 'linkedTaskId 只有便條可以使用' });
  }

  if (task.progress !== null && (task.progress < 0 || task.progress > 100)) {
    violations.push({ code: 'T9', message: 'progress 必須在 0 至 100 之間' });
  }

  if (task.cancelReason !== null && !hasCancelled) {
    violations.push({ code: 'T10', message: 'cancelReason 只有在已取消時可以有值' });
  }

  return violations;
}

export function assertTask(task: Task): void {
  const violations = checkTask(task);
  if (violations.length > 0) throw new InvariantError(violations);
}

export function checkSupervisorReminder(reminder: SupervisorReminder): InvariantViolation[] {
  const violations: InvariantViolation[] = [];

  if ((reminder.state === 'snoozed') !== (reminder.snoozedUntilLocal !== null)) {
    violations.push({ code: 'R1', message: 'state 為 snoozed 時必須有 snoozedUntilLocal' });
  }

  if ((reminder.state === 'done') !== (reminder.completedAt !== null)) {
    violations.push({ code: 'R2', message: 'state 為 done 時必須有 completedAt' });
  }

  if (reminder.followUpPromptDismissedAt !== null && reminder.state !== 'done') {
    violations.push({
      code: 'R3',
      message: 'followUpPromptDismissedAt 只有在 state 為 done 時可以有值',
    });
  }

  return violations;
}

export function assertSupervisorReminder(reminder: SupervisorReminder): void {
  const violations = checkSupervisorReminder(reminder);
  if (violations.length > 0) throw new InvariantError(violations);
}

export function checkBoardColumns(columns: readonly BoardColumn[]): InvariantViolation[] {
  const violations: InvariantViolation[] = [];

  const queues = columns.filter((column) => column.kind === 'completed_queue');
  if (queues.length !== 1) {
    violations.push({
      code: 'C1',
      message: `必須恰有一個完成佇列，目前有 ${queues.length} 個`,
    });
  }

  if (queues.some((queue) => queue.archivedAt !== null)) {
    violations.push({ code: 'C2', message: '完成佇列不可封存，也不可刪除' });
  }

  const activeNormal = columns.filter(
    (column) => column.kind === 'normal' && column.archivedAt === null,
  );
  if (activeNormal.length === 0) {
    violations.push({ code: 'C3', message: '至少要保留一個啟用中的一般欄位' });
  }

  const orders = new Set(columns.map((column) => column.order));
  if (orders.size !== columns.length) {
    violations.push({ code: 'C4', message: 'order 必須在所有欄位間唯一' });
  }

  return violations;
}

export interface PlacementContext {
  /** 所有一般欄位的 id。 */
  columnIds: readonly Uuid[];
  /** 完成佇列的 id。 */
  queueId: Uuid;
}

export function checkPlacements(
  placements: readonly BoardPlacement[],
  context: PlacementContext,
): InvariantViolation[] {
  const violations: InvariantViolation[] = [];
  const validColumns = new Set<Uuid>([...context.columnIds, context.queueId]);

  const seenTasks = new Set<Uuid>();
  for (const placement of placements) {
    if (seenTasks.has(placement.taskId)) {
      violations.push({ code: 'P1', message: '每個任務最多只能有一筆看板位置' });
    }
    seenTasks.add(placement.taskId);

    if (!validColumns.has(placement.columnId)) {
      violations.push({ code: 'P2', message: 'columnId 必須指向存在的欄位' });
    }

    if (placement.columnIdBeforeCompletion !== null && placement.columnId !== context.queueId) {
      violations.push({
        code: 'P3',
        message: 'columnIdBeforeCompletion 只有在卡片位於完成佇列時可以有值',
      });
    }
  }

  return violations;
}
